import React from "react";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { IconSizes } from "atoms/constants/icon-sizes";
import PageBlockToolbarWrapper from "molecules/page-block-content/page-block-toolbar-wrapper";
import PageBlockContextMenu from "organisms/page-blocks/page-block-list/page-block-context-menu";
import { t } from "utilities/localization-utils";
import { ImageSize } from "constants/notebooks/image-size";
import { ImagePosition } from "constants/notebooks/image-position";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";

interface ImageEditorToolbarProps {
    hasImageFile: boolean;
    size: ImageSize;
    position: ImagePosition;
    onSizeChange: (size: ImageSize) => void;
    onPositionChange: (position: ImagePosition) => void;
    onReplaceImage: () => void;
    onDone: () => Promise<void>;
    onDelete: () => Promise<void>;
    onMoveDown: () => Promise<void>;
    onMoveUp: () => Promise<void>;
    setAnnotationMode: () => void;
}

const CSS_CLASS_NAME = "c-image-editor-toolbar";

const ImageEditorToolbar: React.FC<ImageEditorToolbarProps> = ({
    hasImageFile,
    size,
    position,
    onSizeChange,
    onPositionChange,
    onReplaceImage,
    onDone,
    onDelete,
    onMoveUp,
    onMoveDown,
    setAnnotationMode,
}) => {
    const { isMobile } = useNotebookBreakpoints();

    return (
        <PageBlockToolbarWrapper onDone={onDone}>
            <div className={`${CSS_CLASS_NAME}`}>
                {hasImageFile && (
                    <>
                        {!isMobile && (
                            <>
                                <Button
                                    cssClassName={`${CSS_CLASS_NAME}__image-size -no-padding -toolbar-font ${size === ImageSize.Small ? "-selected" : ""} `}
                                    style={ButtonStyles.TertiaryAlt}
                                    onClick={() =>
                                        onSizeChange(ImageSize.Small)
                                    }>
                                    {t("small")}
                                </Button>
                                <div
                                    className={`${CSS_CLASS_NAME}__separator`}
                                />
                                <Button
                                    cssClassName={`${CSS_CLASS_NAME}__image-size -no-padding ${size === ImageSize.Medium ? "-selected" : ""}`}
                                    style={ButtonStyles.TertiaryAlt}
                                    onClick={() =>
                                        onSizeChange(ImageSize.Medium)
                                    }>
                                    {t("medium")}
                                </Button>
                                <div
                                    className={`${CSS_CLASS_NAME}__separator`}
                                />
                                <Button
                                    cssClassName={`${CSS_CLASS_NAME}__image-size -no-padding ${size === ImageSize.Large ? "-selected" : ""}`}
                                    style={ButtonStyles.TertiaryAlt}
                                    onClick={() =>
                                        onSizeChange(ImageSize.Large)
                                    }>
                                    {t("large")}
                                </Button>
                                <div
                                    className={`${CSS_CLASS_NAME}__separator`}
                                />

                                <div
                                    className={`${CSS_CLASS_NAME}__text-align`}>
                                    <Button
                                        cssClassName={`${CSS_CLASS_NAME}__text-align -alignment-button-padding ${position === ImagePosition.Left ? "-selected" : ""}`}
                                        onClick={() =>
                                            onPositionChange(ImagePosition.Left)
                                        }
                                        style={ButtonStyles.TertiaryAlt}
                                        size={ButtonSizes.XSmall}>
                                        <Icon
                                            cssClassName={`${CSS_CLASS_NAME}__text-align__left`}
                                            type={Icons.TextAlignLeft}
                                        />
                                    </Button>
                                    <Button
                                        onClick={() =>
                                            onPositionChange(
                                                ImagePosition.Center
                                            )
                                        }
                                        style={ButtonStyles.TertiaryAlt}
                                        size={ButtonSizes.XSmall}
                                        cssClassName={`${CSS_CLASS_NAME}__text-align  -alignment-button-padding ${position === ImagePosition.Center ? "-selected" : ""}`}>
                                        <Icon
                                            type={Icons.TextAlignCenter}
                                            cssClassName={`${CSS_CLASS_NAME}__text-align__center`}
                                        />
                                    </Button>

                                    <Button
                                        onClick={() =>
                                            onPositionChange(
                                                ImagePosition.Right
                                            )
                                        }
                                        style={ButtonStyles.TertiaryAlt}
                                        size={ButtonSizes.XSmall}
                                        cssClassName={`${CSS_CLASS_NAME}__text-align  -alignment-button-padding ${position === ImagePosition.Right ? "-selected" : ""}`}>
                                        <Icon
                                            type={Icons.TextAlignLeft}
                                            cssClassName={`${CSS_CLASS_NAME}__text-align__right`}
                                        />
                                    </Button>
                                </div>
                                <div
                                    className={`${CSS_CLASS_NAME}__separator`}
                                />
                            </>
                        )}
                        <div className={`${CSS_CLASS_NAME}__image-actions`}>
                            <Button
                                style={ButtonStyles.TertiaryAlt}
                                onClick={onReplaceImage}
                                cssClassName={`${CSS_CLASS_NAME}__image-action`}>
                                <Icon
                                    type={Icons.ImageReplace}
                                    size={IconSizes.Medium}
                                />
                            </Button>
                            <Button
                                style={ButtonStyles.TertiaryAlt}
                                onClick={setAnnotationMode}
                                cssClassName={`${CSS_CLASS_NAME}__image-action`}>
                                <Icon
                                    type={Icons.PencilPlus}
                                    size={IconSizes.Medium}
                                />
                            </Button>
                        </div>
                        <div className={`${CSS_CLASS_NAME}__separator`} />
                    </>
                )}
                <PageBlockContextMenu
                    inline
                    mobileOnly
                    onDelete={onDelete}
                    onMoveDown={onMoveDown}
                    onMoveUp={onMoveUp}
                />
            </div>
        </PageBlockToolbarWrapper>
    );
};

export default ImageEditorToolbar;
